<template>
  <div class="bar">
    <span v-for="hk in primary" :key="hk.key" class="hint">
      <KbdChip :binding="hk.key" />
      <span class="hintLabel">{{ hk.description }}</span>
    </span>

    <button type="button" class="allBtn" @click="open = true">
      <KbdChip binding="?" />
      <span class="hintLabel">all shortcuts</span>
    </button>
  </div>

  <!-- Help dialog. Lists every non-hidden shortcut, grouped. -->
  <div v-if="open" class="overlay" @click.self="open = false">
    <div class="dialog" role="dialog" aria-modal="true" aria-label="Keyboard shortcuts">
      <div class="dialogHead">
        <span class="dialogTitle">Keyboard shortcuts</span>
        <button type="button" class="close" aria-label="Close" @click="open = false">✕</button>
      </div>
      <div class="dialogBody">
        <div v-for="[group, items] in grouped" :key="group" class="group">
          <div v-if="group" class="groupTitle">{{ group }}</div>
          <div v-for="entry in items" :key="entry.description" class="row">
            <span class="rowLabel">{{ entry.description }}</span>
            <span class="rowKeys">
              <template v-for="(key, ki) in entry.keys" :key="key">
                <span v-if="ki > 0" class="orSep">or</span>
                <KbdChip :binding="key" />
              </template>
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { type Hotkey } from '../composables/useHotkeys'
import KbdChip from './KbdChip.vue'

/**
 * Footer shortcut bar plus the `?` help dialog.
 *
 * Renders the SAME array the page passes to useHotkeys, so the bar and dialog
 * can't drift from what the keys actually do — adding a shortcut surfaces it
 * automatically. `primary` picks what's compact enough for the bar; everything
 * else lives in the dialog.
 */

const props = defineProps<{ hotkeys: Hotkey[] }>()

const open = ref(false)

const visible = computed(() => props.hotkeys.filter((h) => !h.hidden && h.description))
const primary = computed(() => visible.value.filter((h) => h.primary))

/**
 * Preserves declaration order within each group, and group order by first use.
 * Collapses bindings that share a description — `Enter` and `o` both "view"
 */
const grouped = computed(() => {
  const map = new Map<string, { description: string; keys: string[] }[]>()
  for (const hk of visible.value) {
    const group = hk.group || ''
    if (!map.has(group)) map.set(group, [])
    const rows = map.get(group)!
    const existing = rows.find((r) => r.description === hk.description)
    if (existing) existing.keys.push(hk.key)
    else rows.push({ description: hk.description!, keys: [hk.key] })
  }
  return [...map.entries()]
})

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && open.value) {
    open.value = false
    return
  }
  // `?` is Shift+/ — registered here rather than through useHotkeys so the
  // dialog owns its own trigger and pages don't each have to wire it.
  if (e.key !== '?') return
  const t = e.target
  if (t instanceof HTMLElement && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return
  e.preventDefault()
  open.value = !open.value
}

onMounted(() => document.addEventListener('keydown', onKeydown))
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown))
</script>

<style scoped>
.bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-4);
  min-width: 0;
}

.hint,
.allBtn {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  color: var(--color-text-secondary);
  line-height: 1;
  white-space: nowrap;
}
.hintLabel { color: var(--color-text-tertiary); }

.allBtn {
  margin-left: auto;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
}
.allBtn:hover .hintLabel { color: var(--color-text-primary); }

/* ── Help dialog ──────────────────────────────────────────────── */
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
}
.dialog {
  width: 420px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 96px);
  display: flex;
  flex-direction: column;
  background: var(--color-bg-surface);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
}
.dialogHead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-4) var(--space-4) var(--space-3);
  border-bottom: 1px solid var(--color-border-subtle);
}
.dialogTitle {
  font-family: var(--font-display);
  font-size: var(--fs-md);
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
}
.close {
  border: none;
  background: none;
  cursor: pointer;
  color: var(--color-text-tertiary);
  font-size: var(--fs-sm);
  padding: 2px 4px;
  line-height: 1;
}
.close:hover { color: var(--color-text-primary); }

.dialogBody {
  padding: var(--space-3) var(--space-4) var(--space-4);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}
.groupTitle {
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
  color: var(--color-text-tertiary);
  margin-bottom: var(--space-2);
}
.row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  padding: var(--space-1) 0;
}
.rowLabel { font-size: var(--fs-sm); color: var(--color-text-primary); }
.rowKeys { display: inline-flex; align-items: center; gap: var(--space-1); flex-shrink: 0; }
.orSep { font-size: var(--fs-xs); color: var(--color-text-tertiary); }
</style>
